import React, { useRef, useEffect, useCallback, useState } from 'react';
import { clamp } from '../hooks/useKaraokeSync';
import './SubtitleLayer.css';

const LOW_CONFIDENCE_THRESHOLD = 0.7;
const WORDS_PER_CHUNK = 2;
const ANTICIPATION_OFFSET_MS = 50;
const PROGRESS_LERP = 0.25; // Per-word lerp smoothing
const TIME_LERP = 0.3; // Visual time smoothing

/**
 * SubtitleLayer - Karaoke subtitle rendering
 *
 * Timing model (fully continuous):
 * - No activeIdx concept — each word computes its own progress independently
 * - Per-word window: [words[i].start, words[i+1].start] or word.end for last word
 * - Progress: (t - start) / (end - start), clamped [0,1]
 * - Per-word lerp smoothing prevents discontinuities at boundaries
 * - Visual time lerp smooths micro-fluctuations in audio.currentTime
 */
const SubtitleLayer = ({
  segments,
  currentTime,
  isPlaying,
  displayOffsetMs = 270,
  crossfadeWindowMs = 150,
  onSegmentEnter,
}) => {
  const containerRef = useRef(null);
  const prevIndexRef = useRef(-1);
  const [renderVersion, setRenderVersion] = useState(0);

  // Per-segment enter time (for animation)
  const segmentEnterTimeRef = useRef(-1);
  const prevSegmentIndexRef = useRef(-1);

  // Per-word smooth progress state: lerp toward target each frame
  const wordProgressRef = useRef({}); // { wordIdx: { target, visual } }

  // Smoothed visual time (lerped to avoid micro-jitter from audio.currentTime)
  const smoothTimeRef = useRef(0);
  const prevSmoothTimeRef = useRef(0);

  const shouldSkipTransitionRef = useRef(false);

  const triggerRender = useCallback(() => {
    setRenderVersion(v => v + 1);
  }, []);

  // Calculate visual time with anticipation offset + time lerp smoothing
  const getVisualTime = useCallback((t) => {
    const offset = ANTICIPATION_OFFSET_MS / 1000;
    const raw = t + offset;
    const smoothed = raw + (prevSmoothTimeRef.current - raw) * TIME_LERP;
    prevSmoothTimeRef.current = smoothTimeRef.current;
    smoothTimeRef.current = raw;
    return smoothed;
  }, []);

  // Linear interpolation
  const lerp = (a, b, t) => a + (b - a) * t;

  /**
   * Fully continuous per-word progress — no activeIdx, no switching.
   *
   * Each word independently computes progress using its own time window:
   *   windowStart = words[i].start
   *   windowEnd   = words[i+1].start  (or words[i].end for last word)
   *
   *   if t <= windowStart → progress = 0
   *   if t >= windowEnd   → progress = 1
   *   else                → progress = (t - windowStart) / (windowEnd - windowStart)
   *
   * Per-word lerp smoothing (PROGRESS_LERP=0.25) keeps each word's progress
   * from jerking — the visual value lerps toward the target each frame.
   *
   * No boundary jumps, no resets, no coupling between words.
   */
  const getWordProgress = useCallback((wordIdx, words, visualTime) => {
    if (!words || words.length === 0) return 0;

    const len = words.length;
    const word = words[wordIdx];

    // Window for this word
    const windowStart = word.start;
    const windowEnd = wordIdx < len - 1 ? words[wordIdx + 1].start : word.end;

    // Compute raw progress
    let raw;
    if (visualTime <= windowStart) {
      raw = 0;
    } else if (visualTime >= windowEnd) {
      raw = 1;
    } else {
      const span = windowEnd - windowStart;
      raw = span > 0 ? (visualTime - windowStart) / span : 1;
    }

    // Lerp toward target for smooth motion
    if (!wordProgressRef.current[wordIdx]) {
      wordProgressRef.current[wordIdx] = { target: 0, visual: 0 };
    }
    const entry = wordProgressRef.current[wordIdx];
    entry.target = raw;
    entry.visual = lerp(entry.visual, entry.target, PROGRESS_LERP);

    return entry.visual;
  }, []);

  // Get word state (discrete, for styling)
  const getWordState = useCallback((word, visualTime) => {
    if (visualTime < word.start) return 'future';
    if (visualTime >= word.end) return 'past';
    return 'active';
  }, []);

  // Get visible segments
  const getVisibleSegments = useCallback(() => {
    if (!segments || segments.length === 0) return { current: null, crossfade: null, prevSeg: null };

    const visualTime = getVisualTime(currentTime);
    const displayOffsetSec = displayOffsetMs / 1000;
    const crossfadeWindowSec = crossfadeWindowMs / 1000;

    let current = null;
    let crossfade = null;
    let prevSeg = null;

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const pauseExt = seg.metadata?.pause_extension_ms || 0;
      const displayStart = seg.start - displayOffsetSec;
      const displayEnd = seg.end + pauseExt / 1000;

      if (visualTime >= displayStart && visualTime <= displayEnd) {
        if (!current) {
          current = { index: i, segment: seg };
        }
      }

      // Crossfade
      if (current && !crossfade && i === current.index + 1) {
        const nextStart = seg.start - displayOffsetSec;
        const crossfadeStart = current.segment.end - crossfadeWindowSec;
        if (visualTime >= crossfadeStart && visualTime < nextStart) {
          crossfade = { index: i, segment: seg };
        }
      }

      // Previous segment
      if (i === current?.index - 1 && current) {
        const prevPause = seg.metadata?.pause_extension_ms || 0;
        const prevEnd = seg.end + prevPause / 1000;
        if (visualTime <= prevEnd + 0.25) {
          prevSeg = { index: i, segment: seg };
        }
      }
    }

    return { current, crossfade, prevSeg };
  }, [segments, currentTime, displayOffsetMs, crossfadeWindowMs, getVisualTime]);

  // Handle segment change
  useEffect(() => {
    const visible = getVisibleSegments();

    if (visible.current) {
      const segIdx = visible.current.index;
      const now = performance.now();

      if (segIdx !== prevIndexRef.current) {
        shouldSkipTransitionRef.current = segIdx === prevIndexRef.current + 1 && (visible.current.segment.start - segments[prevIndexRef.current]?.end) * 1000 < 120;
        prevIndexRef.current = segIdx;
        segmentEnterTimeRef.current = now;
        triggerRender();

        if (onSegmentEnter) {
          onSegmentEnter(visible.current.segment);
        }
      }
    }
  }, [getVisibleSegments, segments, triggerRender, onSegmentEnter]);

  // Chunk words
  const chunkWords = useCallback((words) => {
    if (!words || words.length === 0) return [];
    const chunks = [];
    for (let i = 0; i < words.length; i += WORDS_PER_CHUNK) {
      chunks.push(words.slice(i, i + WORDS_PER_CHUNK));
    }
    return chunks;
  }, []);

  // Render chunk - uses word-start interpolation for smooth cursor
  const renderChunk = useCallback((chunk, chunkStartIdx, visualTime, words) => {
    return chunk.map((word, localIdx) => {
      const wordIdx = chunkStartIdx + localIdx;
      const progress = getWordProgress(wordIdx, words, visualTime);
      const state = getWordState(word, visualTime);
      const isLowConfidence = word.confidence !== undefined && word.confidence < LOW_CONFIDENCE_THRESHOLD;

      return (
        <span
          key={wordIdx}
          className={`subtitle-word subtitle-word-${state} ${isLowConfidence ? 'subtitle-word-low-confidence' : ''}`}
          style={{ '--progress': progress }}
        >
          {word.word}
        </span>
      );
    });
  }, [getWordProgress, getWordState]);

  // Render segment
  const renderSegment = useCallback((seg, index, mode = 'current') => {
    if (!seg) return null;

    const visualTime = getVisualTime(currentTime);
    const isActive = mode === 'current';
    const isCrossfade = mode === 'crossfade';
    const isPrev = mode === 'prev';

    let opacity = 1;
    let translateY = 0;

    if (isCrossfade) {
      const prevSeg = segments[index - 1];
      if (prevSeg) {
        const crossfadeStart = prevSeg.end - crossfadeWindowMs / 1000;
        const crossfadeProgress = clamp((visualTime - crossfadeStart) / (crossfadeWindowMs / 1000), 0, 1);
        opacity = crossfadeProgress;
        translateY = -3 * (1 - opacity);
      }
    }

    if (isPrev) {
      opacity = 0.25;
      translateY = 3;
    }

    const skipTransition = isActive && shouldSkipTransitionRef.current;
    const words = seg.words || [];
    const chunks = chunkWords(words);

    return (
      <div
        key={`${mode}-${index}`}
        className={`subtitle-segment subtitle-segment-${mode} ${!skipTransition && isActive ? 'subtitle-segment-enter' : ''}`}
        style={{
          opacity,
          transform: `translateY(${translateY}px)`,
          pointerEvents: isActive ? 'auto' : 'none',
        }}
      >
        <div className="subtitle-words">
          {chunks.map((chunk, chunkIdx) => (
            <span key={chunkIdx} className="subtitle-word-chunk">
              {renderChunk(chunk, chunkIdx * WORDS_PER_CHUNK, visualTime, words)}
            </span>
          ))}
        </div>
      </div>
    );
  }, [currentTime, segments, crossfadeWindowMs, chunkWords, renderChunk, getVisualTime]);

  if (!segments || segments.length === 0) {
    return null;
  }

  const visible = getVisibleSegments();

  return (
    <div key={renderVersion} ref={containerRef} className="subtitle-layer">
      {visible.prevSeg && (
        <div className="subtitle-preview subtitle-preview-prev">
          {renderSegment(visible.prevSeg.segment, visible.prevSeg.index, 'prev')}
        </div>
      )}

      {visible.current && (
        <div className="subtitle-current">
          {renderSegment(visible.current.segment, visible.current.index, 'current')}
        </div>
      )}

      {visible.crossfade && (
        <div className="subtitle-preview subtitle-preview-next">
          {renderSegment(visible.crossfade.segment, visible.crossfade.index, 'crossfade')}
        </div>
      )}
    </div>
  );
};

export default SubtitleLayer;
